import React, {useEffect, useRef, useState} from 'react';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  LinearProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {FirebaseError} from "firebase/app";
import {ConfirmationResult, onAuthStateChanged} from "firebase/auth";
import {
  clearPhoneRecaptcha,
  confirmPhoneSignIn,
  getLoggedInUserOrEmpty,
  isAccountNotAssignedError,
  preparePhoneRecaptcha,
  provisionUser,
  sendPasswordReset,
  signInWithEmail,
  signInWithGoogle,
  signOutUser,
  startPhoneSignIn,
} from "../../shared/services/auth.service";
import {logError, logInfo, setLogContext} from "../../shared/services/logger.service";
import {firebaseAuth} from "../../shared/services/firebase.service";
import {toE164} from "../../shared/phone";
import {
  closeAccountCopy,
  forgetClosedNotice,
  formatEraseOn,
  readClosedNotice,
} from "../settings/account-closure.service";

const RECAPTCHA_ID = 'phone-signin-recaptcha';

const friendlyError = (error: unknown): string => {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        // Closing an account is what disables its sign-in.
        return closeAccountCopy.closedDisabled;
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'Incorrect email or password.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled.';
      case 'auth/invalid-phone-number':
      case 'auth/missing-phone-number':
        return 'Please enter a valid phone number.';
      case 'auth/invalid-verification-code':
        return 'That code is incorrect. Try again.';
      case 'auth/code-expired':
        return 'That code expired. Send a new one.';
      case 'auth/captcha-check-failed':
        return 'Phone verification failed. Please try again.';
      case 'auth/quota-exceeded':
        return 'Too many phone sign-in attempts. Please try again later.';
      case 'auth/operation-not-allowed':
        return 'Phone sign-in is not enabled. Ask an administrator to turn it on.';
      case 'auth/invalid-app-credential':
      case 'auth/app-not-authorized':
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
          return 'Firebase does not send phone codes from localhost. Use the local test number (208) 555-0100 / 123456, or open the app from 127.0.0.1 or staging.savaquan.com.';
        }
        return 'Phone verification was rejected for this site. Try again from the app’s web address.';
      default:
        return 'Unable to sign in. Please try again.';
    }
  }
  return 'Unable to sign in. Please try again.';
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [closedEraseOn, setClosedEraseOn] = useState(readClosedNotice);
  const recaptchaReady = useRef(false);
  // A sign-in made on this page goes Home only once the server has provisioned
  // it (completeSignIn). Firebase announces the new user before that, and
  // leaving on that announcement would drop the server's refusal, such as a
  // closed account's, which this page shows.
  const signingIn = useRef(false);

  // Shown once: the notice comes from closing the account just now.
  useEffect(() => {
    forgetClosedNotice();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        if (!signingIn.current) {
          navigate('/home', {replace: true});
        }
        return;
      }
      setCheckingSession(false);
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (checkingSession || recaptchaReady.current) {
      return;
    }
    try {
      preparePhoneRecaptcha(RECAPTCHA_ID);
      recaptchaReady.current = true;
    } catch (e) {
      logError('Phone recaptcha setup failed', {}, e as Error);
    }
    return () => {
      clearPhoneRecaptcha();
      recaptchaReady.current = false;
    };
  }, [checkingSession]);

  const completeSignIn = async () => {
    try {
      await provisionUser(navigate);
    } catch (e) {
      // Only a refusal (no team, a closed account) keeps the person here.
      // Anything else (a 500, the network) must not strand a good sign-in on
      // this page: Home's first company request provisions again.
      if (isAccountNotAssignedError(e)) {
        throw e;
      }
      logError('Provisioning after sign-in failed', {}, e as Error);
    }
    const identity = getLoggedInUserOrEmpty();
    setLogContext({user: identity});
    logInfo('Login successful');
    navigate('/home');
  };

  const handleSignInError = async (context: string, e: unknown) => {
    if (isAccountNotAssignedError(e)) {
      await signOutUser().catch(() => undefined);
      setError(e.message);
      return;
    }
    logError(context, {}, e as Error);
    setError(friendlyError(e));
  };

  const handleEmailSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    signingIn.current = true;
    try {
      await signInWithEmail(email, password);
      await completeSignIn();
    } catch (e) {
      await handleSignInError('Email login error', e);
    } finally {
      signingIn.current = false;
      setSubmitting(false);
    }
  };

  // SQ-537: password reset for email accounts. A phone account has no
  // password to reset: the next code is its way back in.
  const handleForgotPassword = async () => {
    setError(null);
    setInfo(null);
    if (!email.trim()) {
      setError('Enter your email above, then choose Forgot password.');
      return;
    }
    setSubmitting(true);
    try {
      await sendPasswordReset(email.trim());
      setInfo(`If an account uses ${email.trim()}, a password reset link is on its way.`);
    } catch (e) {
      logError('Password reset error', {}, e as Error);
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setSubmitting(true);
    signingIn.current = true;
    try {
      await signInWithGoogle();
      await completeSignIn();
    } catch (e) {
      await handleSignInError('Google login error', e);
    } finally {
      signingIn.current = false;
      setSubmitting(false);
    }
  };

  const handleSendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const e164 = toE164(phone);
    if (!e164) {
      setError('Please enter a valid phone number.');
      return;
    }
    setSubmitting(true);
    try {
      if (!recaptchaReady.current) {
        preparePhoneRecaptcha(RECAPTCHA_ID);
        recaptchaReady.current = true;
      }
      const result = await startPhoneSignIn(e164);
      setConfirmation(result);
      setCode('');
    } catch (e) {
      recaptchaReady.current = false;
      try {
        preparePhoneRecaptcha(RECAPTCHA_ID);
        recaptchaReady.current = true;
      } catch {
        /* next attempt will recreate */
      }
      await handleSignInError('Phone code send error', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!confirmation) {
      return;
    }
    setError(null);
    setSubmitting(true);
    signingIn.current = true;
    try {
      await confirmPhoneSignIn(confirmation, code.trim());
      await completeSignIn();
    } catch (e) {
      await handleSignInError('Phone code confirm error', e);
    } finally {
      signingIn.current = false;
      setSubmitting(false);
    }
  };

  if (checkingSession) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
        <Box sx={{width: '100%', maxWidth: 400}}>
          <Typography variant="body1" align="center" gutterBottom>
            Signing you in…
          </Typography>
          <LinearProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
      <Paper elevation={3} sx={{p: 4, width: '100%', maxWidth: 400}}>
        <Typography variant="h5" align="center" gutterBottom>
          Sign in to SavaQuan
        </Typography>

        {closedEraseOn && (
          <Alert
            severity="success"
            // OK sits under the text rather than beside it, so a phone-width
            // card keeps the support address on one line; one narrower still
            // wraps it rather than clipping it.
            sx={{mb: 2, '& .MuiAlert-message': {flexGrow: 1, overflowWrap: 'anywhere'}}}
          >
            <AlertTitle sx={{fontWeight: 'bold'}}>{closeAccountCopy.doneTitle}</AlertTitle>
            {closeAccountCopy.doneBody(formatEraseOn(closedEraseOn))}
            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1}}>
              <Button color="inherit" size="small" onClick={() => setClosedEraseOn(null)}>OK</Button>
            </Box>
          </Alert>
        )}
        {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
        {info && <Alert severity="success" sx={{mb: 2}}>{info}</Alert>}

        <Box id={RECAPTCHA_ID} />

        {mode === 'email' ? (
          <Box component="form" onSubmit={handleEmailSignIn}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting}
              sx={{mt: 2}}
            >
              Sign in
            </Button>
            <Button
              fullWidth
              size="small"
              disabled={submitting}
              onClick={handleForgotPassword}
              sx={{mt: 1}}
            >
              Forgot password?
            </Button>
          </Box>
        ) : confirmation ? (
          <Box component="form" onSubmit={handleConfirmCode}>
            <Typography color="text.secondary" sx={{mb: 1}}>
              Enter the code sent to {phone}.
            </Typography>
            <TextField
              label="Verification code"
              fullWidth
              margin="normal"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              inputProps={{inputMode: 'numeric'}}
              required
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting || !code.trim()}
              sx={{mt: 2}}
            >
              Verify code
            </Button>
            <Button
              fullWidth
              disabled={submitting}
              onClick={() => { setConfirmation(null); setCode(''); setError(null); }}
              sx={{mt: 1}}
            >
              Use a different number
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSendCode}>
            <TextField
              label="Phone number"
              type="tel"
              fullWidth
              margin="normal"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              required
            />
            <Typography variant="caption" color="text.secondary" display="block">
              No password: we text you a fresh code each time you sign in.
            </Typography>
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" color="text.secondary" display="block">
                Localhost cannot receive a real SMS. Use (208) 555-0100, then code 123456.
              </Typography>
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting}
              sx={{mt: 2}}
            >
              Send code
            </Button>
          </Box>
        )}

        <Button
          fullWidth
          disabled={submitting}
          onClick={() => {
            setMode(mode === 'email' ? 'phone' : 'email');
            setError(null);
            setInfo(null);
            setConfirmation(null);
            setCode('');
          }}
          sx={{mt: 1}}
        >
          {mode === 'email' ? 'Sign in with phone' : 'Sign in with email'}
        </Button>

        <Divider sx={{my: 3}}>or</Divider>

        <Button
          variant="outlined"
          fullWidth
          disabled={submitting}
          onClick={handleGoogleSignIn}
        >
          Continue with Google
        </Button>

        <Typography variant="body2" align="center" sx={{mt: 3}}>
          New to SavaQuan?{' '}
          <Link component={RouterLink} to="/signup">Create an account</Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default LoginPage;
